'use client'

import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime'
import { useContext, useEffect, useRef } from 'react'

interface NextHistoryState {
  __N?: boolean
  url?: string
  as?: string
  options?: Record<string, unknown>
  key?: string
}

const getCurrentAsPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`

export const useRestoreNextHistoryState = () => {
  const router = useContext(RouterContext)
  const nextEntryRef = useRef<NextHistoryState | null>(null)

  useEffect(() => {
    if (!router) {
      return
    }

    const entryOnMount = window.history.state as NextHistoryState | null

    if (entryOnMount?.__N && !nextEntryRef.current) {
      nextEntryRef.current = entryOnMount
    }

    const restoreEntryBeforeLeaving = () => {
      const nextEntry = nextEntryRef.current

      if (!nextEntry) {
        return
      }

      const currentEntry = window.history.state as NextHistoryState | null

      if (currentEntry?.__N) {
        return
      }

      const asPath = getCurrentAsPath()
      const isSameEntry = nextEntry.as === asPath
      const isRouteWithoutDynamicSegments = nextEntry.url === nextEntry.as

      window.history.replaceState(
        {
          ...currentEntry,
          key: isSameEntry ? nextEntry.key : (currentEntry?.key ?? nextEntry.key),
          __N: true,
          url: isRouteWithoutDynamicSegments ? asPath : nextEntry.url,
          as: asPath,
          options: nextEntry.options,
        },
        '',
        asPath,
      )
    }

    router.events.on('beforeHistoryChange', restoreEntryBeforeLeaving)

    return () => {
      router.events.off('beforeHistoryChange', restoreEntryBeforeLeaving)
    }
  }, [router])
}
